import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import {
    Grid, Paper, Typography, Box, Chip, LinearProgress, IconButton, Button, Tooltip
} from '@mui/material';
import {
    Thermostat, Opacity, Air, WaterDrop, Lightbulb, LightbulbOutlined,
    Refresh, Settings as SettingsIcon, FileDownload as ExportIcon
} from '@mui/icons-material';
import { useWebSocket } from '../../hooks/useWebSocket';
import { useApp } from '../../context/AppContext';
import { api } from '../../services/api';
import SensorCard from './SensorCard';
import RelayControl from './RelayControl';
import PlantCard from './PlantCard';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { STAGE_LABELS } from '../../utils/labels';

const Dashboard = () => {
    const navigate = useNavigate();
    const { selectedDeviceId } = useApp();
    const [deviceData, setDeviceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [sensors, setSensors] = useState({ temperature: 0, humidity: 0 });
    const [relays, setRelays] = useState({});
    const [plants, setPlants] = useState([]);
    const [stats, setStats] = useState({ lightingHours: 0, lastWatering: null });
    const [watering, setWatering] = useState({ last: null, next: null });

    const { lastMessage, sendMessage } = useWebSocket();

    useEffect(() => { loadData(); const i = setInterval(loadData, 30000); return () => clearInterval(i); }, [selectedDeviceId]);

    useEffect(() => {
        if (!lastMessage) return;
        try {
            const data = JSON.parse(lastMessage);
            if (data.type === 'sensor_update') setSensors({ temperature: data.temperature, humidity: data.humidity });
            else if (data.type === 'relay_update') setRelays(data.relays);
        } catch (e) { console.error(e); }
    }, [lastMessage]);

    const loadData = async () => {
        try {
            setLoading(true);
            const [statusRes, sensorsRes, plantsRes, configRes] = await Promise.all([
                api.get(`/device/status?deviceId=${selectedDeviceId}`).catch(() => null),
                api.get(`/sensors/latest?deviceId=${selectedDeviceId}`).catch(() => null),
                api.get(`/plants?deviceId=${selectedDeviceId}`).catch(() => null),
                api.get(`/device/config?deviceId=${selectedDeviceId}`).catch(() => null)
            ]);
            if (statusRes?.data) setDeviceData(statusRes.data);
            if (sensorsRes?.data) {
                setSensors({ temperature: sensorsRes.data.temperature, humidity: sensorsRes.data.humidity });
                setRelays(sensorsRes.data.relays || {});
            }
            if (plantsRes?.data) setPlants(plantsRes.data);

            // Calcula última e próxima rega
            const config = configRes?.data?.config || {};
            const intervalH = config.irrigationInterval || 12;
            const last = sensorsRes?.data?.lastWatering
                || plantsRes?.data?.[0]?.lastWatering
                || stats.lastWatering;
            let next = null;
            if (last) {
                next = new Date(new Date(last).getTime() + intervalH * 3600 * 1000);
            }
            setWatering({ last: last ? new Date(last) : null, next });
        } catch (e) {
            console.error('Erro ao carregar dados:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleRelayToggle = async (relay, state) => {
        try {
            await api.post('/commands', { deviceId: selectedDeviceId, command: 'set_relay', params: { relay, state } });
            sendMessage(JSON.stringify({ type: 'relay_control', relay, state }));
            setRelays(prev => ({ ...prev, [relay]: state }));
        } catch (e) { console.error(e); }
    };

    if (loading) return <Box sx={{ width: '100%', mt: 4 }}><LinearProgress /></Box>;

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <Paper sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box
                                component="img"
                                src="/logo.png"
                                alt="AutoGrow"
                                sx={{ width: 40, height: 40, mr: 1.5 }}
                            />
                            <Box>
                                <Typography variant="h4">AutoGrow</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    {new Date().toLocaleString('pt-BR')}
                                </Typography>
                            </Box>
                        </Box>
                        <Box>
                            <Chip label={deviceData?.status === 'online' ? 'Online' : 'Offline'}
                                color={deviceData?.status === 'online' ? 'success' : 'error'} sx={{ mr: 1 }} />
                            <IconButton onClick={loadData}><Refresh /></IconButton>
                            <IconButton onClick={() => navigate('/settings')}><SettingsIcon /></IconButton>
                        </Box>
                    </Paper>
                </Grid>

                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <SensorCard title="Temperatura" value={sensors.temperature} unit="°C" icon={<Thermostat />} color="#FF6B6B" />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <SensorCard title="Umidade" value={sensors.humidity} unit="%" icon={<Opacity />} color="#2196F3" />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <SensorCard title="VPD" value={sensors.vpd || 0} unit="kPa" icon={<Air />} color="#9C27B0" />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={3}>
                    <SensorCard title="Ponto de Orvalho" value={sensors.dewPoint || 0} unit="°C" icon={<WaterDrop />} color="#00BCD4" />
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>Relés / Controles</Typography>
                        <Grid container spacing={2}>
                            <RelayControl label="Luz Fria" state={relays[0]} onToggle={(s) => handleRelayToggle(0, s)} icon={<LightbulbOutlined />} color="primary" />
                            <RelayControl label="Luz Quente" state={relays[1]} onToggle={(s) => handleRelayToggle(1, s)} icon={<Lightbulb />} color="primary" />
                            <RelayControl label="Rega" state={relays[2]} onToggle={(s) => handleRelayToggle(2, s)} icon={<WaterDrop />} color="secondary" />
                            <RelayControl label="Ventilação" state={relays[3]} onToggle={(s) => handleRelayToggle(3, s)} icon={<Air />} color="secondary" />
                            <RelayControl label="Umidificador" state={relays[4]} onToggle={(s) => handleRelayToggle(4, s)} icon={<Opacity />} color="secondary" />
                            <RelayControl label="Exaustor" state={relays[5]} onToggle={(s) => handleRelayToggle(5, s)} icon={<Air />} color="secondary" />
                        </Grid>
                    </Paper>
                </Grid>

                <Grid item xs={12}>
                    <Paper sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                            <Typography variant="h6">Plantas</Typography>
                            <Button variant="outlined" startIcon={<ExportIcon />} onClick={() => exportHistory(plants)} disabled={exporting}>
                                {exporting ? 'Exportando...' : 'Exportar histórico'}
                            </Button>
                        </Box>
                        <Grid container spacing={2}>
                            {plants.map(p => <Grid item xs={12} sm={6} md={4} key={p._id}><PlantCard plant={p} /></Grid>)}
                            {plants.length === 0 && <Grid item xs={12}><Typography color="textSecondary">Nenhuma planta cadastrada.</Typography></Grid>}
                        </Grid>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

function exportHistory(plants) {
    const rows = plants.map(p => ({
        Nome: p.name,
        Strain: p.strain,
        Estagio: p.growthStage,
        Inicio: p.startDate,
        Fim: p.endDate,
        PesoUmido: p.wetWeight,
        PesoSeco: p.dryWeight
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Historico');
    XLSX.writeFile(wb, 'autogrow_historico.xlsx');
}

export default Dashboard;
